import React from "react";
import { Link } from "react-router-dom";
import { MessageCircle, Instagram, Facebook, Mail, Phone, MapPin } from "lucide-react";
import { BRAND, ASSETS, waUrl } from "@/lib/brand";

const QUICK_LINKS = [
  { label: "Home", to: "/" },
  { label: "Forest Honey", to: "/forest-honey" },
  { label: "A2 Cow Ghee", to: "/a2cow-ghee" },
  { label: "Why Rudrima", to: "/#why" },
  { label: "Reviews", to: "/#reviews" },
  { label: "FAQ", to: "/#faq" },
];

const LEGAL_LINKS = [
  { label: "Track Order", to: "/track-order" },
  { label: "Contact Us", to: "/contact-us" },
  { label: "Shipping Policy", to: "/shipping-policy" },
  { label: "Refund & Return Policy", to: "/refund-and-return-policy" },
  { label: "Terms & Conditions", to: "/terms-and-conditions" },
  { label: "Privacy Policy", to: "/privacy-policy" },
];

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

export const Footer = ({ bottomPad = "pb-28 lg:pb-10" }) => (
  <footer className={`bg-[#0F2A12] text-white/85 pt-16 ${bottomPad} relative overflow-hidden`} data-testid="site-footer">
    <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#D4A017]/40 to-transparent"></div>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-10">
        <div className="sm:col-span-2">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-11 h-11 rounded-full bg-white/95 border border-[#D4A017]/40 flex items-center justify-center overflow-hidden">
              <img src={ASSETS.logo} alt="Rudrima" className="w-full h-full object-contain" />
            </div>
            <div>
              <div className="font-display text-2xl font-bold text-white">Rudrima</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#E8BE4A] font-semibold">Pure · Natural · Healthy</div>
            </div>
          </div>
          <p className="text-white/70 text-sm max-w-md leading-relaxed">
            Rudrima brings you the purest organic forest honey and traditional A2 Bilona cow ghee — sourced with respect for nature, packed with love and delivered fresh to your family.
          </p>
          <div className="flex items-center gap-3 mt-5">
            <a href={waUrl()} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" className="w-10 h-10 rounded-full bg-[#25D366] hover:scale-110 transition-transform flex items-center justify-center"><MessageCircle className="w-4 h-4 text-white" /></a>
            <a href={BRAND.instagram} aria-label="Instagram" className="w-10 h-10 rounded-full bg-white/10 hover:bg-[#D4A017] transition-all flex items-center justify-center"><Instagram className="w-4 h-4" /></a>
            <a href={BRAND.facebook} aria-label="Facebook" className="w-10 h-10 rounded-full bg-white/10 hover:bg-[#D4A017] transition-all flex items-center justify-center"><Facebook className="w-4 h-4" /></a>
            <a href={`mailto:${BRAND.email}`} aria-label="Email" className="w-10 h-10 rounded-full bg-white/10 hover:bg-[#D4A017] transition-all flex items-center justify-center"><Mail className="w-4 h-4" /></a>
          </div>
        </div>

        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-[#E8BE4A] font-bold mb-4">Quick Links</div>
          <ul className="space-y-2.5 text-sm">
            {QUICK_LINKS.map((l) => (
              <li key={l.label}><Link to={l.to} data-testid={`footer-link-${slug(l.label)}`} className="hover:text-white text-white/70 transition-colors">{l.label}</Link></li>
            ))}
          </ul>
        </div>

        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-[#E8BE4A] font-bold mb-4">Help &amp; Policies</div>
          <ul className="space-y-2.5 text-sm" data-testid="footer-legal-links">
            {LEGAL_LINKS.map((l) => (
              <li key={l.label}><Link to={l.to} data-testid={`footer-link-${slug(l.label)}`} className="hover:text-white text-white/70 transition-colors">{l.label}</Link></li>
            ))}
          </ul>
        </div>

        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-[#E8BE4A] font-bold mb-4">Get in Touch</div>
          <ul className="space-y-3 text-sm">
            <li className="flex items-start gap-2"><Phone className="w-4 h-4 text-[#E8BE4A] mt-0.5" /> <a href={waUrl()} target="_blank" rel="noopener noreferrer" className="hover:text-white text-white/80">{BRAND.whatsappDisplay}</a></li>
            <li className="flex items-start gap-2"><Mail className="w-4 h-4 text-[#E8BE4A] mt-0.5" /> <a href={`mailto:${BRAND.email}`} className="hover:text-white text-white/80">{BRAND.email}</a></li>
            <li className="flex items-start gap-2"><MapPin className="w-4 h-4 text-[#E8BE4A] mt-0.5 shrink-0" /> <address className="not-italic text-white/80 leading-relaxed" data-testid="footer-address">{BRAND.company}<br />{BRAND.addressLines[0]}<br />{BRAND.addressLines[1]}</address></li>
          </ul>
        </div>
      </div>

      <div className="mt-12 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-white/50 text-center sm:text-left">
        <div>© {new Date().getFullYear()} Rudrima. All rights reserved.</div>
        <div data-testid="footer-company-line" className="text-white/70 font-semibold tracking-wide">{BRAND.company} · Lucknow</div>
        <div>Crafted with care · Pure. Natural. Healthy.</div>
      </div>
    </div>
  </footer>
);
